export const BADGE_GREEN = "#07BD09";
export const BADGE_YELLOW = "#E0A93F";
export const BADGE_RED = "#D32F2F";
export const BADGE_GREY = "#9E9E9E";

// A finished order is history, not a queue entry. Colouring it by age would
// make every delivered row from yesterday red and the colour would stop
// meaning "act on this".
const SETTLED_STATUSES = ["delivered", "cancelled", "declined"];

export const isSettled = (statusName) =>
  SETTLED_STATUSES.includes(String(statusName || "").trim().toLowerCase());

const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

const describeElapsed = (minutes) => {
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${plural(Math.floor(minutes), "minute")} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${plural(hours, "hour")} ago`;
  return `${plural(Math.floor(hours / 24), "day")} ago`;
};

const describeRemaining = (minutes) => {
  if (minutes < 60) return `${Math.ceil(minutes)} m to go`;
  const hours = Math.floor(minutes / 60);
  const rest = Math.round(minutes % 60);
  return rest === 0 ? `${hours} h to go` : `${hours} h ${rest} m to go`;
};

const minutesBetween = (later, earlier) =>
  (later.getTime() - earlier.getTime()) / 60000;

export const DEFAULT_CANCEL_WINDOW_MINUTES = 1;

export const describeCancelCountdown = (secondsLeft) => {
  const whole = Math.ceil(Math.round(secondsLeft * 1000) / 1000);
  if (whole <= 60) return `${whole}s to cancel`;
  const minutes = Math.floor(whole / 60);
  const seconds = whole % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")} to cancel`;
};

const parsed = (value) => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

/**
 * How long an order has been sitting. Boundaries are inclusive on the lower
 * bound, so no elapsed value falls through without a colour -- the helper this
 * replaces returned undefined at exactly 5 and exactly 15 minutes, and the
 * caller then read .color off it and threw.
 */
export const placedAgeBadge = (
  orderDate,
  now = new Date(),
  statusName,
  cancelWindowMinutes = DEFAULT_CANCEL_WINDOW_MINUTES
) => {
  const placed = parsed(orderDate);
  if (!placed) return null;

  const minutes = Math.max(minutesBetween(parsed(now) || new Date(), placed), 0);
  const settled = isSettled(statusName);
  const secondsLeftToCancel = Math.min(
    (cancelWindowMinutes - minutes) * 60,
    cancelWindowMinutes * 60
  );
  const text =
    !settled && secondsLeftToCancel > 0
      ? describeCancelCountdown(secondsLeftToCancel)
      : describeElapsed(minutes);

  if (settled) return { text, color: BADGE_GREY, minutes };
  if (minutes > 35) return { text, color: BADGE_RED, minutes };
  if (minutes >= 25) return { text, color: BADGE_YELLOW, minutes };
  return { text, color: BADGE_GREEN, minutes };
};

/**
 * How long until a scheduled slot opens. Closer means more urgent, so the
 * colours run the other way from placedAgeBadge.
 */
export const scheduledCountdownBadge = (slot, now = new Date(), statusName) => {
  if (!slot || !slot.scheduled_date) return null;

  const date = String(slot.scheduled_date).slice(0, 10);
  const time = slot.from_time || "00:00:00";
  const start = parsed(`${date}T${time}`);
  if (!start) return null;

  const minutes = minutesBetween(start, parsed(now) || new Date());

  if (isSettled(statusName)) {
    return { text: describeRemaining(Math.max(minutes, 0)), color: BADGE_GREY, minutes };
  }
  if (minutes < 0) {
    const late = Math.abs(minutes);
    const text =
      late < 60
        ? `overdue by ${Math.floor(late)} m`
        : `overdue by ${Math.floor(late / 60)} h`;
    return { text, color: BADGE_RED, minutes };
  }

  const text = describeRemaining(minutes);
  if (minutes <= 60) return { text, color: BADGE_RED, minutes };
  if (minutes <= 90) return { text, color: BADGE_YELLOW, minutes };
  return { text, color: BADGE_GREEN, minutes };
};

export const resolveCancelWindow = (item, pageWindowMinutes) =>
  item && Number.isFinite(item.cancel_window_minutes)
    ? item.cancel_window_minutes
    : pageWindowMinutes;

/** Scheduled rows show the countdown only; the age of one is not actionable. */
export const orderTimeBadge = (
  item,
  now = new Date(),
  cancelWindowMinutes = DEFAULT_CANCEL_WINDOW_MINUTES
) => {
  const statusName = item && item.status ? item.status.name : null;
  if (item && item.scheduled_slot) {
    return scheduledCountdownBadge(item.scheduled_slot, now, statusName);
  }
  return placedAgeBadge(
    item && item.date,
    now,
    statusName,
    resolveCancelWindow(item, cancelWindowMinutes)
  );
};

export const EDITED_COLOR = "#E65100";

export const editedLabel = (editCount) => {
  const count = Number(editCount) || 0;
  if (count <= 0) return "";
  return count === 1 ? "Edited" : `Edited ×${count}`;
};

